import { Link } from "react-router-dom";
import { Heart, Shield, Users, DollarSign, Activity, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteForm from "@/components/QuoteForm";

const benefits = [
  { icon: Shield, title: "Proteção Familiar", desc: "Garanta segurança financeira para sua família em caso de imprevistos." },
  { icon: DollarSign, title: "Planejamento Financeiro", desc: "O seguro de vida é parte essencial de um bom planejamento financeiro." },
  { icon: Users, title: "Educação dos Filhos", desc: "Assegure o futuro educacional dos seus filhos independente do que aconteça." },
  { icon: Activity, title: "Doenças Graves", desc: "Cobertura para tratamento de doenças graves com indenização antecipada." },
];

export default function SeguroVida() {
  return (
    <div data-testid="seguro-vida-page">
      {/* Hero */}
      <section className="relative pt-20">
        <div className="bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-5">
                <Heart className="h-3.5 w-3.5 text-gold" />
                <span className="text-xs text-white/80 font-medium">Seguro de Vida</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-5">
                Seguro de Vida: <span className="text-gold">proteção</span> para quem você ama
              </h1>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
                Garanta a segurança financeira da sua família com planos acessíveis e coberturas completas. Seguro de vida a partir de R$ 50/mês.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/cotacao"><Button className="btn-gold h-11 px-6" data-testid="vida-cta-cotacao">Solicitar Cotação <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
                <a href="https://wa.me/553125550207?text=Quero%20saber%20sobre%20seguro%20de%20vida" target="_blank" rel="noopener noreferrer">
                  <Button className="btn-whatsapp h-11 px-6" data-testid="vida-cta-whatsapp">Falar com Especialista</Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Benefícios</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy">Por que contratar seguro de vida?</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="flex gap-5 bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                  <b.icon className="h-6 w-6 text-brand" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy mb-1.5">{b.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverages + Form */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Coberturas disponíveis</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-6">O que o seguro de vida pode cobrir</h2>
              <ul className="space-y-3">
                {[
                  "Morte natural ou acidental",
                  "Invalidez permanente total ou parcial",
                  "Doenças graves (câncer, infarto, AVC)",
                  "Diárias por internação hospitalar",
                  "Assistência funeral familiar",
                  "Renda por incapacidade temporária",
                  "Dupla indenização por morte acidental",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-brand shrink-0 mt-0.5" /> <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-500 italic font-accent">
                "O seguro de vida não é para quem morre. É para quem fica."
              </p>
            </div>
            <div className="bg-white rounded-2xl border p-6 md:p-8 shadow-sm">
              <h3 className="font-heading font-semibold text-navy text-xl mb-5">Cotação - Seguro de Vida</h3>
              <QuoteForm preselect="Seguro de Vida" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
